import React, { useState, useEffect, useMemo } from 'react';

const CATEGORIES = ['Registration Fee', 'Materials', 'Penalty', 'Misc'];

const PaymentForm = ({
  initialData = {},
  onSubmit,
  onCancel,
  students = [],
  intakes = [],
  studentEnrollments = [],
  isEdit = false,
  loading = false,
  error = null,
  onStudentChange,
  contextStudentId = '' // when present, lock + preselect this student
}) => {
  // Form state
  const [formData, setFormData] = useState({
    student_id: '',
    payment_type: 'Individual',
    payment_method: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    status: 'Pending',
    notes: '',
    enrollment_ids: [],
    intake_id: '',
    invoice_number: ''
  });

  // Other charges (UI only; saved by parent via onSubmit)
  const [otherCharges, setOtherCharges] = useState([]);
  const [otherChargesError, setOtherChargesError] = useState('');
  const [formLevelError, setFormLevelError] = useState('');

  // Derived
  const hasAnyEnrollments = (studentEnrollments?.length || 0) > 0;

  // Initialize form with initial data when provided
  useEffect(() => {
    if (Object.keys(initialData).length > 0) {
      setFormData({
        student_id: initialData.student_id || '',
        payment_type: initialData.payment_type || 'Individual',
        payment_method: initialData.payment_method || '',
        amount: initialData.amount || '',
        payment_date: initialData.payment_date
          ? new Date(initialData.payment_date).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        status: initialData.status || 'Pending',
        notes: initialData.notes || '',
        enrollment_ids: (initialData.enrollment_ids || []).map(String), // normalize
        intake_id: initialData.intake_id || '',
        invoice_number: initialData.invoice_number || ''
      });

      if (Array.isArray(initialData.other_charges) && initialData.other_charges.length > 0) {
        setOtherCharges(
          initialData.other_charges.map(oc => ({
            id: oc.id || `local-${crypto.randomUUID?.() || Date.now()}`,
            category: oc.category || '',
            description: oc.description || '',
            amount: String(oc.amount ?? '')
          }))
        );
      }
    }
  }, [initialData]);

  // In Student Context: lock student id only (do NOT fetch from here)
  useEffect(() => {
    if (!contextStudentId) return;
    setFormData(prev => ({ ...prev, student_id: contextStudentId }));
  }, [contextStudentId]);

  // Update enrollment_ids when intake_id changes for Full payments
  useEffect(() => {
    if (formData.intake_id && formData.payment_type === 'Full') {
      const enrollmentsForIntake = studentEnrollments.filter(
        enrollment => String(enrollment.intake_id) === String(formData.intake_id)
      );
      setFormData(prev => ({
        ...prev,
        enrollment_ids: enrollmentsForIntake.map(e => String(e.id))
      }));
    }
  }, [formData.intake_id, formData.payment_type, studentEnrollments]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'payment_type') {
      // Reset Full-specific selections when switching types
      if (value === 'Individual') {
        setFormData(prev => ({ ...prev, intake_id: '' }));
      } else if (value === 'Full') {
        setFormData(prev => ({ ...prev, enrollment_ids: [] }));
      }
    }
    setFormLevelError('');
  };

  // Handle student selection change (ignored when contextStudentId is set)
  const handleStudentChange = async (e) => {
    const studentId = e.target.value;
    setFormData(prev => ({
      ...prev,
      student_id: studentId,
      enrollment_ids: [],
      intake_id: ''
    }));
    if (studentId && onStudentChange) {
      await onStudentChange(studentId);
    }
  };

  // Handle enrollment selection (for Individual payments) — normalize IDs to strings
  const handleEnrollmentChange = (e) => {
    const selected = Array.from(e.target.selectedOptions).map(opt => String(opt.value));
    setFormData(prev => ({
      ...prev,
      enrollment_ids: selected
    }));
    setFormLevelError('');
  };

  // Handle intake selection (for Full payments)
  const handleIntakeChange = (e) => {
    const intakeId = e.target.value;
    setFormData(prev => ({
      ...prev,
      intake_id: intakeId
    }));
    setFormLevelError('');
  };

  // Other Charges helpers
  const addOtherChargeRow = () => {
    setOtherCharges(prev => [
      ...prev,
      { id: `local-${crypto.randomUUID?.() || Date.now()}`, category: '', description: '', amount: '' }
    ]);
    setFormLevelError('');
  };

  const removeOtherChargeRow = (id) => {
    setOtherCharges(prev => prev.filter(r => r.id !== id));
  };

  const updateOtherChargeField = (id, field, value) => {
    setOtherCharges(prev =>
      prev.map(r => (r.id === id ? { ...r, [field]: value } : r))
    );
    setFormLevelError('');
  };

  const otherChargesSubtotal = useMemo(() => {
    return otherCharges.reduce((sum, r) => {
      const n = Number(r.amount);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [otherCharges]);

  // Filter enrollments by selected intake (for Full payments)
  const filteredEnrollments = formData.intake_id
    ? studentEnrollments.filter(enrollment => String(enrollment.intake_id) === String(formData.intake_id))
    : studentEnrollments;

  // Stable listbox semantics
  const listboxSize = Math.min(8, Math.max(4, filteredEnrollments.length || 0));

  // Validate Other Charges if any rows exist
  const validateOtherCharges = () => {
    if (otherCharges.length === 0) return true;
    for (const r of otherCharges) {
      const amt = Number(r.amount);
      if (!r.category || !r.description || !Number.isFinite(amt) || !(amt > 0)) {
        setOtherChargesError('Please complete all Other Charges rows with a valid amount > 0.');
        return false;
      }
    }
    setOtherChargesError('');
    return true;
  };

  // Global item validation: Must have at least one item — either enrollments OR other charges
  const hasSelectedEnrollments = (formData.enrollment_ids?.length || 0) > 0;
  const hasOtherCharges = (otherCharges.length || 0) > 0;

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate other charges rows
    if (!validateOtherCharges()) {
      document.getElementById('other-charges-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Require at least one item overall
    if (!hasSelectedEnrollments && !hasOtherCharges) {
      setFormLevelError('Please select at least one enrollment or add at least one Other Charge to proceed.');
      // Focus either the enrollments list (if available) or the Other Charges section
      if (filteredEnrollments.length > 0) {
        document.getElementById('enrollment_ids')?.focus();
      } else {
        document.getElementById('other-charges-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    const paymentData = {
      ...formData,
      amount: parseFloat(formData.amount || 0),
      otherCharges: otherCharges.map(({ id, category, description, amount }) => ({
        category,
        description,
        amount: Number(amount || 0),
      })),
    };
    onSubmit(paymentData);
  };

  // Resolve the locked student's display (for the read-only info)
  const lockedStudent = contextStudentId
    ? students.find(s => String(s.id) === String(contextStudentId))
    : null;

  const paymentTypeIsFull = formData.payment_type === 'Full';
  const paymentTypeIsIndividual = formData.payment_type === 'Individual';

  // When there are no enrollments, prevent selecting "Full"
  const disableFullOption = !hasAnyEnrollments;

  // For the Individual enrollments select, only require selection if there are enrollments
  // AND user has not added any other charges.
  const individualEnrollmentsRequired =
    paymentTypeIsIndividual && filteredEnrollments.length > 0 && !hasOtherCharges;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {(error || formLevelError) && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error || formLevelError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Student Selection */}
          {!isEdit && !contextStudentId && (
            <div>
              <label htmlFor="student_id" className="block text-sm font-medium text-gray-700 mb-1">
                Student *
              </label>
              <select
                id="student_id"
                name="student_id"
                value={formData.student_id}
                onChange={handleStudentChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select a student</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.full_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Read-only student when in context */}
          {contextStudentId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Student
              </label>
              <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-700">
                {lockedStudent ? lockedStudent.full_name : contextStudentId}
              </div>
              <input type="hidden" name="student_id" value={formData.student_id} />
            </div>
          )}

          {/* Payment Type (only in create mode) */}
          {!isEdit && (
            <div>
              <label htmlFor="payment_type" className="block text-sm font-medium text-gray-700 mb-1">
                Payment Type *
              </label>
              <select
                id="payment_type"
                name="payment_type"
                value={formData.payment_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="Individual">Individual</option>
                <option value="Full" disabled={disableFullOption}>
                  Full {disableFullOption ? '(no enrollments available)' : ''}
                </option>
              </select>
            </div>
          )}

          {/* Payment Method */}
          <div>
            <label htmlFor="payment_method" className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method *
            </label>
            <select
              id="payment_method"
              name="payment_method"
              value={formData.payment_method}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">Select a method</option>
              <option value="Online Transfer">Online Transfer</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Check">Check</option>
              <option value="Others">Others</option>
            </select>
          </div>

          {/* Amount */}
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
              Amount (RM) *
            </label>
            <input
              type="number"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              min="0"
              step="0.01"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Tip: This should equal Enrollments total + Other Charges.
            </p>
          </div>

          {/* Payment Date */}
          <div>
            <label htmlFor="payment_date" className="block text-sm font-medium text-gray-700 mb-1">
              Payment Date *
            </label>
            <input
              type="date"
              id="payment_date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status *
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>

          {/* Invoice Number */}
          <div>
            <label htmlFor="invoice_number" className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Number *
            </label>
            <input
              type="text"
              id="invoice_number"
              name="invoice_number"
              value={formData.invoice_number}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              placeholder="Enter invoice number"
            />
          </div>
        </div>

        {/* Conditional Fields Based on Payment Type (only in create mode) */}
        {!isEdit && paymentTypeIsFull && (
          <div className="mt-6">
            {hasAnyEnrollments ? (
              <>
                <label htmlFor="intake_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Intake *
                </label>
                <select
                  id="intake_id"
                  name="intake_id"
                  value={formData.intake_id}
                  onChange={handleIntakeChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select an intake</option>
                  {intakes.map(intake => (
                    <option key={intake.id} value={intake.id}>
                      {intake.name}
                    </option>
                  ))}
                </select>
                {formData.intake_id && (
                  <p className="mt-2 text-sm text-gray-500">
                    This payment will cover {filteredEnrollments.length} enrollment(s) for this intake.
                  </p>
                )}
              </>
            ) : (
              <div className="p-3 rounded bg-yellow-50 text-yellow-700 text-sm">
                No enrollments available to cover. Select <b>Individual</b> and add <b>Other Charges</b> instead.
              </div>
            )}
          </div>
        )}

        {!isEdit && paymentTypeIsIndividual && (
          <div className="mt-6">
            <label htmlFor="enrollment_ids" className="block text-sm font-medium text-gray-700 mb-1">
              Enrollments {individualEnrollmentsRequired ? '*' : '(optional)'}
            </label>

            {filteredEnrollments.length > 0 ? (
              <>
                <select
                  id="enrollment_ids"
                  name="enrollment_ids"
                  multiple
                  size={listboxSize}
                  value={(formData.enrollment_ids || []).map(String)}
                  onChange={handleEnrollmentChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required={individualEnrollmentsRequired}
                >
                  {filteredEnrollments.map(enrollment => (
                    <option key={enrollment.id} value={String(enrollment.id)}>
                      {enrollment.subjects?.name || enrollment.subject?.name} - {enrollment.intakes?.name || enrollment.intake?.name}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-sm text-gray-500">
                  Hold Ctrl/Cmd to select multiple enrollments.
                </p>
              </>
            ) : (
              <div className="p-3 rounded bg-blue-50 text-blue-700 text-sm">
                No unpaid enrollments to select. You can still proceed by adding <b>Other Charges</b> below.
              </div>
            )}
          </div>
        )}

        {/* Other Charges (Items Builder) */}
        <div id="other-charges-section" className="mt-8">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Other Charges</h3>
            <button
              type="button"
              onClick={addOtherChargeRow}
              className="px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
            >
              + Add Charge
            </button>
          </div>

          {otherChargesError && (
            <div className="mt-3 p-3 rounded bg-red-100 text-red-700 text-sm">
              {otherChargesError}
            </div>
          )}

          {otherCharges.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">No other charges added.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (RM)</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {otherCharges.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2">
                        <select
                          value={row.category}
                          onChange={(e) => updateOtherChargeField(row.id, 'category', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">Select</option>
                          {CATEGORIES.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={row.description}
                          onChange={(e) => updateOtherChargeField(row.id, 'description', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                          placeholder="Describe the charge"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={row.amount}
                          onChange={(e) => updateOtherChargeField(row.id, 'amount', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeOtherChargeRow(row.id)}
                          className="px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                          aria-label="Remove charge"
                          title="Remove charge"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50">
                    <td className="px-3 py-2 text-right font-medium" colSpan={2}>Subtotal</td>
                    <td className="px-3 py-2 font-semibold">
                      RM{otherChargesSubtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
              <p className="mt-2 text-xs text-gray-500">
                Ensure the overall <strong>Amount (RM)</strong> above equals Enrollments total + Other Charges subtotal.
              </p>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="mt-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows="3"
          />
        </div>

        {/* Form Actions */}
        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
          >
            {loading ? 'Saving...' : isEdit ? 'Update Payment' : 'Create Payment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;
